import { apiRequest } from './httpClient';

// Same apiRequest as every other service (authApi.js, meetingApi.js,
// adminApi.js) - same base URL, same credentials/cookie behavior, same
// 401 refresh-and-retry. Nothing here has its own HTTP configuration.

// PUBLIC: works logged out (backend/routers/pricing.py has no auth
// dependency), which is exactly why the pricing page can render for a
// visitor who has never signed up. Returns active plans only, cheapest
// first. An empty array is a valid response - it means no plans are
// configured yet, not an error.
export function getPublicPricing() {
  return apiRequest('/api/pricing');
}

// The signed-in user's OWN plan + usage. Returns
// { enforced, subscription, lifetime_minutes } - see
// backend/schemas/subscription.py's UsageSummaryOut for what the two
// null/false cases mean.
export function getMySubscription() {
  return apiRequest('/api/subscriptions/me');
}

// billingCycle is required - a plan may offer monthly, yearly, or both
// (backend/models/pricing_plan.py), so which one the subscriber wants
// has to be said explicitly rather than assumed.
//
// Does NOT change the subscriber's active plan - it creates a PENDING
// request that only takes effect once an admin approves it (backend/
// services/subscription_service.py's request_plan_change). Returns the
// request, not a subscription.
export function requestPlanChange(planId, billingCycle) {
  return apiRequest('/api/subscriptions', {
    method: 'POST',
    body: { plan_id: planId, billing_cycle: billingCycle },
  });
}

// The caller's own PENDING request, or null if they have none.
export function getMyPendingRequest() {
  return apiRequest('/api/subscriptions/me/request');
}

// Machine-readable codes the backend attaches to a 402 (see
// backend/services/subscription_service.py). Branch on THESE, never on
// the human-readable message, which is copy and may be reworded.
export const USAGE_ERROR_CODES = {
  NO_ACTIVE_SUBSCRIPTION: 'NO_ACTIVE_SUBSCRIPTION',
  // Legacy: minutes and meeting duration are no longer plan limits, so
  // the backend no longer raises these two. Kept so a response from an
  // older backend is still recognised.
  MINUTES_LIMIT_REACHED: 'MINUTES_LIMIT_REACHED',
  MEETING_DURATION_EXCEEDED: 'MEETING_DURATION_EXCEEDED',
  MEETING_COUNT_LIMIT_REACHED: 'MEETING_COUNT_LIMIT_REACHED',
  // AI work done OUTSIDE a metered meeting (Ask KrishvaQ, memory
  // search, document processing). Two distinct codes because they need
  // different answers: one means "upgrade to get this at all", the
  // other means "you have used up what you have".
  AI_FEATURE_NOT_IN_PLAN: 'AI_FEATURE_NOT_IN_PLAN',
  AI_LIMIT_REACHED: 'AI_LIMIT_REACHED',
  PLAN_NOT_AVAILABLE: 'PLAN_NOT_AVAILABLE',
  BILLING_CYCLE_NOT_AVAILABLE: 'BILLING_CYCLE_NOT_AVAILABLE',
};

export function isUsageLimitError(error) {
  return Boolean(error) && Object.values(USAGE_ERROR_CODES).includes(error.code);
}

// Machine-readable codes the backend attaches to a 409 for a plan-
// change REQUEST specifically (services/subscription_service.py's
// PlanChangeRequestError) - a business-rule conflict, not a usage
// limit, so it is deliberately a separate set from USAGE_ERROR_CODES
// above rather than folded into it.
export const PLAN_CHANGE_ERROR_CODES = {
  PLAN_ALREADY_ACTIVE: 'PLAN_ALREADY_ACTIVE',
  PLAN_CHANGE_REQUEST_PENDING: 'PLAN_CHANGE_REQUEST_PENDING',
  // A paid user asked for a LOWER plan (or yearly -> monthly, or free)
  // while the period they already paid for is still running. Raised by
  // BOTH the checkout and the plan-change-request endpoints.
  DOWNGRADE_NOT_ALLOWED_DURING_ACTIVE_PERIOD: 'DOWNGRADE_NOT_ALLOWED_DURING_ACTIVE_PERIOD',
};

export function isPlanChangeConflict(error) {
  return Boolean(error) && Object.values(PLAN_CHANGE_ERROR_CODES).includes(error.code);
}

// Display and plan-selection helpers - shared with Desktop
// (@copilot/ui's billing.ts), so a price, a meeting allowance or a
// plan-selection button is never formatted two slightly different ways.
export {
  CUSTOM_PLAN,
  PAY_PER_MEETING,
  STANDARD_MEETING_DURATION,
  availableBillingCycles,
  classifyPlanChange,
  customPlanDescription,
  describeHiddenLowerPlans,
  describePlanSelection,
  filterPlansForCurrentPeriod,
  formatAiRequests,
  formatBillingCycle,
  formatBillingCycleLabel,
  formatMeetingCap,
  formatMeetingLimit,
  formatMinutes,
  formatPassDuration,
  formatPassMeetings,
  formatPassPrice,
  formatPlanPrice,
  getSubscriptionPresentation,
  isDowngradeLike,
  isFreePlan,
  isHiddenByPaidPeriod,
  payPerMeetingBuyLabel,
  payPerMeetingFeatures,
  planDescription,
  PLAN_FEATURES,
} from '@copilot/ui';
